import { z } from 'zod';

/**
 * Scene package format produced by the offline renderer (tools/scene-renderer) and
 * consumed by the 2D compositor (packages/render-2d).
 *
 * Encoding conventions (see docs/asset-production-guide.md for the full description):
 *  - `background`: display-referred (tone-mapped, sRGB) colour of everything that is NOT a
 *    swappable surface, averaged over the non-swappable samples of each pixel.
 *  - `diffuse`, `reflGloss`, `reflSatin`: HDR lighting passes stored as an 8-bit "mantissa"
 *    image (sRGB-like gamma) plus a shared low-resolution `scale` map:
 *        value = pow(mantissa, hdr.gamma) * pow(2, mix(scaleMin, scaleMax, scaleSample))
 *    where scaleSample is the bilinearly filtered scale map (R = diffuse, G = gloss, B = satin).
 *  - `surfaceMap` (lossless): R = surface id A, G = surface id B (0 = background),
 *    B = coverage weight of A (0..255). Weight of B = 1 - weight(A).
 *  - Surfaces are planar. `h` is a homography mapping normalised image coordinates
 *    (x right, y down, 0..1) to surface coordinates in metres, where the surface V axis
 *    follows the material grain direction.
 */

export const Lighting = z.enum(['day', 'night']);
export type Lighting = z.infer<typeof Lighting>;

export const SceneLightingFiles = z.object({
  exposure: z.number(),
  background: z.string(),
  diffuse: z.string(),
  reflGloss: z.string(),
  reflSatin: z.string(),
  scale: z.string(),
});
export type SceneLightingFiles = z.infer<typeof SceneLightingFiles>;

export const SceneSurface = z.object({
  id: z.number().int().min(1).max(255),
  name: z.string(),
  zone: z.string().nullable(),
  h: z.array(z.number()).length(9),
  uvOffset: z.tuple([z.number(), z.number()]),
});
export type SceneSurface = z.infer<typeof SceneSurface>;

export const SceneTier = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  dir: z.string(),
});
export type SceneTier = z.infer<typeof SceneTier>;

export const DoorPlane = z.object({
  /** Pixels per millimetre on the wall plane, at the master resolution. */
  pxPerMm: z.number().positive(),
  /** Floor line on the wall plane, in master pixels from the top. */
  floorY: z.number(),
  /** Horizontal centre of the door opening, in master pixels from the left. */
  centerX: z.number(),
  /** Surface id of the wall that receives the door. */
  surfaceId: z.number().int(),
  /** Dominant light direction on the wall (x right, y up, z out of the wall), unit length. */
  keyLight: z.tuple([z.number(), z.number(), z.number()]),
});
export type DoorPlane = z.infer<typeof DoorPlane>;

export const SceneManifest = z.object({
  format: z.literal('cfg-scene@1'),
  key: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  tiers: z.array(SceneTier).min(1),
  tonemap: z.literal('aces'),
  hdr: z.object({
    scaleMin: z.number(),
    scaleMax: z.number(),
    scaleDownsample: z.number().int().positive(),
    gamma: z.number().positive(),
  }),
  surfaceMap: z.string(),
  preview: z.string(),
  lightings: z.object({ day: SceneLightingFiles, night: SceneLightingFiles.optional() }),
  surfaces: z.array(SceneSurface),
  zones: z.array(z.string()),
  doorPlane: DoorPlane.optional(),
  generator: z.string(),
});
export type SceneManifest = z.infer<typeof SceneManifest>;

export function tierFor(manifest: SceneManifest, targetWidth: number): SceneTier {
  const sorted = [...manifest.tiers].sort((a, b) => a.width - b.width);
  return sorted.find((t) => t.width >= targetWidth) ?? sorted[sorted.length - 1]!;
}
